import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Check, CircleDot } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { getTenantToday } from '../../../utils/dateUtils';
import {
  apiErrorMessage,
  calculateFacilityValuation,
  createFacilityPriceEvidence,
  createTenantScenarioDefinition,
  decideFacilityValuation,
  getMonitoringWorkspace,
  runFacilityScenario,
} from '../api/abfApi';
import { type ActionFeedbackState } from '../components/ActionFeedback';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { fmtDate, humanizeToken } from '../presentation/format';
import { Button, Card, CardHead, INK, Row, fieldBase } from '../onboarding/review/reviewStyles';
import { Layout, Note, Readable, Signoff, SmallButton, TaskButton, TaskRail, Work } from '../onboarding/review/contractReviewStyles';
import type { MonitoringWorkspaceReadModel } from '../types/round3';
import { DateField } from '../../../components/shared/DateField';

/**
 * Slice 4 — stress and valuation, on their own route and on the review standard.
 *
 * These two jobs sat below the covenant panel on Monitoring, competing with a breach for
 * the same first screen while being a different job with a different maker and checker.
 * Every comparable platform files valuation as its own module: Setpoint puts it under a
 * different top-level group from facility monitoring, 73 Strings ships it as a separately
 * deployed application, and Cardo AI has no valuation surface on Deal Surveillance at all.
 * The operator settled it on 2026-09-16 and FR-4.8 was amended.
 *
 * The first move of that change carried the old markup across unchanged, which is why the
 * tab looked wrong. This is the rebuild: the same journey strip, metric strip, task rail,
 * work pane and sticky decision bar as Period review, Funding and Monitoring.
 *
 * The governance is untouched. A proposal is evidence. Only an independently approved mark
 * calls the MTM lifecycle lane, and it supersedes the position rather than editing it.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.55; margin-top: 14px;
  h2, h3, p { margin: 0; } h2 { font-size: 17px; } h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;

const Journey = styled.ol`
  display: grid; grid-template-columns: repeat(3, 1fr); list-style: none;
  padding: 0; margin: 0 0 18px; border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden;
  li { display: flex; gap: 12px; align-items: center; padding: 13px 16px; background: #0c1626; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[data-current='true'] { background: #12273c; box-shadow: inset 0 -2px ${INK.accent}; }
  .step-icon { display: grid; place-items: center; width: 28px; height: 28px; border: 1px solid ${INK.lineStrong}; border-radius: 50%; color: #b5c9dd; flex-shrink: 0; }
  strong { display: block; font-size: 14px; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
  @media (max-width: 700px) { grid-template-columns: 1fr; li + li { border-left: 0; border-top: 1px solid ${INK.line}; } }
`;

const Strip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr)); gap: 1px;
  margin-bottom: 18px; border: 1px solid ${INK.line}; border-radius: 10px;
  background: ${INK.line}; overflow: hidden;
  > div { padding: 11px 14px; background: ${INK.panel}; }
  > div[data-tone='bad'] { background: #1d1518; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }
  strong { display: block; margin: 5px 0 2px; color: ${INK.strong}; font-size: 19px; font-weight: 800; }
  strong[data-tone='bad'] { color: #fca5a5; } strong[data-tone='good'] { color: #7cddbe; }
  small { color: ${INK.muted}; font-size: 12px; line-height: 1.4; }
`;

const Facts = styled.dl`
  display: grid; grid-template-columns: minmax(8rem, auto) 1fr; gap: 8px 16px; margin: 0;
  dt { color: ${INK.faint}; font-size: 12px; font-weight: 750; }
  dd { margin: 0; color: ${INK.body}; font-size: 13px; }
`;

const Fields = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px; margin-bottom: 16px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const Field = styled.label<{ $wide?: boolean }>`
  display: flex; min-width: 0; flex-direction: column; gap: 6px;
  color: ${INK.body}; font-size: 13px; font-weight: 650;
  ${p => p.$wide ? 'grid-column: 1 / -1;' : ''}
  input, select, textarea { ${fieldBase}; min-width: 0; width: 100%; box-sizing: border-box; }
  textarea { min-height: 86px; resize: vertical; }
  small { color: ${INK.muted}; font-weight: 400; font-size: 12px; line-height: 1.45; }
`;

const InputHeading = styled.div`
  margin: 2px 0 18px;
  h3 { color: ${INK.strong}; font-size: 16px; }
  p { margin-top: 4px; color: ${INK.muted}; font-size: 13px; line-height: 1.5; }
`;

const InputBody = styled(Readable)`
  max-width: 980px;
`;

const InputActions = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
  padding-top: 14px; border-top: 1px solid ${INK.line};
  span { color: ${INK.muted}; font-size: 12px; line-height: 1.45; }
`;

const Bar = styled.footer`
  position: sticky; bottom: 0; display: flex; flex-wrap: wrap; gap: 12px;
  align-items: center; justify-content: space-between; margin-top: 16px;
  padding: 12px 16px; border: 1px solid ${INK.line}; border-radius: 10px;
  background: rgba(10, 18, 30, .94); backdrop-filter: blur(6px);
  .state { color: ${INK.muted}; font-size: 13px; }
  .actions { display: flex; flex-wrap: wrap; gap: 8px; }
`;

const Grid2 = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr)); gap: 14px; margin-top: 14px;
`;

const Failure = styled.p`
  margin: 0 0 .7rem; padding: .55rem .65rem;
  border: 1px solid rgba(248,113,113,.4); border-radius: 8px;
  background: rgba(127,29,29,.18); color: #fecaca; font-size: .72rem; line-height: 1.5;
`;

// ---------------------------------------------------------------------------

const money = (v: unknown, ccy: string) => v == null ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(Number(v));
const pct = (v: unknown) => v == null ? '—' : `${Number(v).toFixed(3)}%`;
const date = (v: unknown) => fmtDate(v == null ? null : String(v));
const plain = (v: unknown, fb = '—') => v == null || v === '' ? fb
  : String(v).replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
const who = (v: unknown) => v ? String(v) : '—';
const stateTone = (state?: string): StatusTone =>
  state === 'APPROVED' ? 'done' : state === 'CHALLENGED' || state === 'REJECTED' ? 'critical'
    : state === 'SUPERSEDED' ? 'neutral' : 'progress';

const normalizedRole = (role?: string) => ({ PM: 'PORTFOLIO_MANAGER', RISK: 'RISK_MANAGER', COMPLIANCE: 'COMPLIANCE_OFFICER', OPERATIONS: 'OPERATIONS_ANALYST' }[String(role || '').toUpperCase()] || String(role || '').toUpperCase());
const checkerRoles = ['RISK_MANAGER', 'COMPLIANCE_OFFICER', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN'];
const makerRoles = ['PORTFOLIO_MANAGER', 'TRADER', 'RISK_MANAGER', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN'];
const inputAdministratorRoles = ['RISK_MANAGER', 'ADMIN', 'PLATFORM_ADMIN'];

const OPEN_STATES = ['CALCULATED', 'REVIEWED'];
const CLOSED_STATES = ['APPROVED', 'CHALLENGED', 'REJECTED', 'SUPERSEDED'];

interface Props {
  officialPosition?: import('../types/facilityWorkspace').OfficialPositionAuthority;
  facilityId: number;
  currency?: string | null;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const ValuationWorkspace: React.FC<Props> = ({ facilityId, currency, onFeedback, officialPosition }) => {
  const { user } = useAuth();
  const [model, setModel] = useState<MonitoringWorkspaceReadModel | null>(null);
  const [selectedId, setSelectedId] = useState<number | 'new'>('new');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asOfDate, setAsOfDate] = useState(getTenantToday());
  const [scenarioId, setScenarioId] = useState('');
  const [priceEvidenceId, setPriceEvidenceId] = useState('');
  const [rationale, setRationale] = useState('');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [scenarioCode, setScenarioCode] = useState('');
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioSeverity, setScenarioSeverity] = useState('ADVERSE');
  const [scenarioCdr, setScenarioCdr] = useState('');
  const [scenarioLossSeverity, setScenarioLossSeverity] = useState('');
  const [scenarioHaircut, setScenarioHaircut] = useState('');
  const [priceDate, setPriceDate] = useState(getTenantToday());
  const [priceMark, setPriceMark] = useState('');
  const [priceType, setPriceType] = useState('INTERNAL_VALUATION_MEMO');
  const [priceIdentifier, setPriceIdentifier] = useState('');
  const [priceQuality, setPriceQuality] = useState('GOOD');
  const [priceSource, setPriceSource] = useState('');
  const [priceNotes, setPriceNotes] = useState('');
  const workRef = useRef<HTMLElement>(null);
  const ccy = currency || 'USD';

  const load = useCallback(async () => {
    try {
      const result = await getMonitoringWorkspace(facilityId);
      setModel(result);
      if (!scenarioId && result.scenarioDefinitions[0]?.id) setScenarioId(String(result.scenarioDefinitions[0].id));
      if (!priceEvidenceId && result.valuationPriceEvidence[0]?.id) {
        const evidence = result.valuationPriceEvidence[0];
        setPriceEvidenceId(String(evidence.id));
      }
      // F-S4-033: a mark rests on a released tape, and Ridgeline's tapes are dated at
      // period ends that fall after the tenant's business date. Defaulting the effective
      // date to today therefore produced a proposal with no tape to rest on, refused by
      // the server for a reason the operator could not have guessed. Default to the newest
      // released tape where today has none.
      const tapeAsOf = (result as any).valuationAuthority?.latestReleasedTapeAsOf;
      if (tapeAsOf) {
        const tape = String(tapeAsOf).slice(0, 10);
        setAsOfDate(prev => (prev < tape ? tape : prev));
      }
      const open = result.valuationRuns?.find((r: any) => OPEN_STATES.includes(r.state));
      if (open) setSelectedId(prev => (prev === 'new' ? open.id : prev));
    } catch (e) {
      onFeedback({ tone: 'error', message: apiErrorMessage(e, 'Unable to load facility valuation.') });
    }
  }, [facilityId, onFeedback, priceEvidenceId, scenarioId]);

  useEffect(() => { void load(); }, [load]);

  const act = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key); setError(null);
    try {
      await action();
      onFeedback({ tone: 'success', message: success });
      setDecisionNotes('');
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, 'The valuation action could not be completed.'));
    } finally { setBusy(null); }
  };

  const runs: any[] = useMemo(() => model?.valuationRuns || [], [model?.valuationRuns]);
  const evidence: any[] = useMemo(() => model?.valuationPriceEvidence || [], [model?.valuationPriceEvidence]);
  const scenario = model?.scenarioRuns?.find((r: any) =>
    String(r.scenario_definition_id) === scenarioId && String(r.as_of_date) === asOfDate);
  const selected: any = selectedId === 'new' ? null : runs.find(r => r.id === selectedId) || null;
  const selectedPrice: any = evidence.find(item => String(item.id) === priceEvidenceId);
  const role = normalizedRole(user?.role);
  const canMake = makerRoles.includes(role);
  const canCheck = checkerRoles.includes(role);
  const canConfigureInputs = inputAdministratorRoles.includes(role);
  const sameActor = Boolean(selected?.created_by && selected.created_by === user?.username);
  // The server owns the policy. Older workspace responses default to the safer requirement.
  const separateReviewerRequired = model?.fourEyesRequired !== false && sameActor;
  const official = runs.find(r => r.state === 'APPROVED');
  const tapeAsOf = (model as any)?.valuationAuthority?.latestReleasedTapeAsOf;

  if (!model) return <Shell><Note>Loading valuation evidence…</Note></Shell>;

  const hasEvidence = evidence.length > 0;
  const hasScenarios = (model.scenarioDefinitions?.length || 0) > 0;
  const proposing = selectedId === 'new' || (selected && CLOSED_STATES.includes(selected.state));
  const decidable = selected && OPEN_STATES.includes(selected.state);
  const journeyStep: 1 | 2 | 3 = decidable ? 3 : hasEvidence ? 2 : 1;

  const select = (id: number | 'new') => {
    setSelectedId(id); setError(null); setDecisionNotes('');
    if (window.matchMedia('(max-width: 1000px)').matches) workRef.current?.scrollIntoView({ block: 'start' });
  };

  const delta = selected && selected.proposed_market_value != null && selected.current_market_value != null
    ? Number(selected.proposed_market_value) - Number(selected.current_market_value) : null;

  const priceEvidenceInputs = (
    <Signoff open={!hasEvidence}>
      <summary>Record price evidence <small>A dated source for a proposed mark</small></summary>
      <InputBody>
        <InputHeading>
          <h3>Price and source</h3>
          <p>Capture the observed mark and the document behind it. Recording evidence leaves the official position unchanged.</p>
        </InputHeading>
        <Fields>
          <Field>Evidence date<DateField value={priceDate} onChange={setPriceDate} /></Field>
          <Field>Mark (% of par)<input type="number" min="0.000001" max="200" step="0.000001" value={priceMark} onChange={e => setPriceMark(e.target.value)} /></Field>
          <Field>Evidence type<select value={priceType} onChange={e => setPriceType(e.target.value)}>
            <option value="INTERNAL_VALUATION_MEMO">Internal valuation memo</option>
            <option value="BROKER_QUOTE">Broker quote</option>
            <option value="TRUSTEE_REPORT">Trustee report</option>
            <option value="VENDOR_MARKET_DATA">Vendor market data</option>
          </select></Field>
          <Field>Data quality<select value={priceQuality} onChange={e => setPriceQuality(e.target.value)}>
            <option value="GOOD">Good / current</option>
            <option value="ESTIMATED">Estimated</option>
            <option value="STALE">Stale</option>
          </select></Field>
          <Field>Provider or document ID<input value={priceIdentifier} onChange={e => setPriceIdentifier(e.target.value)} placeholder="Immutable source identifier" /></Field>
          <Field>Source reference<input value={priceSource} onChange={e => setPriceSource(e.target.value)} placeholder="Document, message or report reference" /></Field>
          <Field $wide>Evidence notes<textarea value={priceNotes} onChange={e => setPriceNotes(e.target.value)} placeholder="The source reviewed and why it applies to this facility." /></Field>
        </Fields>
        <InputActions>
          <Button type="button" disabled={Boolean(busy) || !canConfigureInputs || !priceDate || !priceMark || !priceIdentifier.trim() || !priceSource.trim() || !priceNotes.trim()}
            onClick={() => void act('price', () => createFacilityPriceEvidence(facilityId, {
              priceDate, markPct: Number(priceMark), evidenceType: priceType,
              providerIdentifier: priceIdentifier.trim(), dataQuality: priceQuality,
              sourceReference: priceSource.trim(), notes: priceNotes.trim(),
            }), 'Price evidence recorded; official IBOR is unchanged.')}>
            {busy === 'price' ? 'Recording…' : 'Record price evidence'}
          </Button>
          {!canConfigureInputs && <span style={{ color: INK.faint, fontSize: 12 }}>A risk or tenant administrator records evidence.</span>}
        </InputActions>
      </InputBody>
    </Signoff>
  );
  return <Shell>
    <Journey aria-label="How a mark changes">
      <li data-current={journeyStep === 1}><span className="step-icon">1</span><div><strong>Evidence is recorded</strong><small>A price with a source, a date and a quality, kept as its own record</small></div></li>
      <li data-current={journeyStep === 2}><span className="step-icon">2</span><div><strong>A mark is proposed</strong><small>Indicative only, naming the evidence it rests on</small></div></li>
      <li data-current={journeyStep === 3}><span className="step-icon">3</span><div><strong>An authorized reviewer approves it</strong><small>{model.fourEyesRequired !== false ? 'Tenant policy requires a different person; approval posts a new position version' : 'Tenant policy permits the proposer to review; approval posts a new position version'}</small></div></li>
    </Journey>

    <Strip aria-label="This facility">
      <div>
        <span>Official mark</span>
        <strong>{pct(officialPosition?.markPct)}</strong>
        <small>{`Official IBOR · as of ${date(officialPosition?.asOfDate)}`}</small>
      </div>
      <div>
        <span>Official market value</span>
        <strong>{money(officialPosition?.marketValue, ccy)}</strong>
        <small>{'Current official position; proposals below are separate'}</small>
      </div>
      <div data-tone={decidable ? 'bad' : undefined}>
        <span>Open proposal</span>
        <strong data-tone={decidable ? 'bad' : 'good'}>{decidable ? plain(selected.state) : 'None'}</strong>
        <small>{decidable ? `Raised by ${who(selected.created_by)}` : 'Nothing is waiting for a decision'}</small>
      </div>
      <div>
        <span>Price evidence</span>
        <strong data-tone={hasEvidence ? undefined : 'bad'}>{evidence.length}</strong>
        <small>{hasEvidence ? `Latest ${date(evidence[0].price_date)}` : 'A proposal needs at least one'}</small>
      </div>
      <div>
        <span>Scenarios</span>
        <strong>{model.scenarioDefinitions?.length || 0}</strong>
        <small>{hasScenarios ? 'Available for stress runs' : 'None defined yet'}</small>
      </div>
    </Strip>

    {!hasEvidence && !hasScenarios && <Note style={{ marginBottom: 14 }}>
      <strong>No governed valuation inputs are on file.</strong>
      <p>Record dated price evidence to propose a mark. Scenario definitions are optional and managed separately.</p>
    </Note>}

    <Layout>
      <TaskRail aria-label="Valuation runs">
        <div>
          <TaskButton type="button" $active={selectedId === 'new'} onClick={() => select('new')}>
            <span className="label">New proposal</span>
            <span className="reason" style={{ color: hasEvidence ? '#7cddbe' : undefined }}>
              {hasEvidence ? <Check size={12} /> : <CircleDot size={12} />}
              {hasEvidence ? 'Evidence is available' : 'Record evidence first'}
            </span>
          </TaskButton>
          {runs.map((r: any) => (
            <TaskButton key={r.id} type="button" $active={selectedId === r.id} onClick={() => select(r.id)}>
              <span className="label">{date(r.effective_date)} · {pct(r.proposed_mark_pct)}</span>
              <span className="reason" style={{ color: OPEN_STATES.includes(r.state) ? '#f0a24a' : undefined }}>
                {OPEN_STATES.includes(r.state) ? <CircleDot size={12} /> : <Check size={12} />}
                {plain(r.state)}{r.lifecycle_trade_id ? ` · trade #${r.lifecycle_trade_id}` : ''}
              </span>
            </TaskButton>
          ))}
          {!runs.length && <p className="empty">No valuation has been proposed on this facility.</p>}
        </div>
      </TaskRail>

      <Work ref={workRef} aria-label="Selected valuation">
        <div className="work-heading">
          <div>
            <h2>{selectedId === 'new' ? hasEvidence ? 'Propose a mark' : 'Record price evidence first' : `${date(selected?.effective_date)} proposal`}</h2>
            <p>A proposal is evidence. Approving it is the only thing that changes the official
              mark, and it posts through the same lifecycle lane a draw uses.</p>
          </div>
          {selected && <StatusChip tone={stateTone(selected.state)} size="md" label={plain(selected.state)} />}
        </div>

        {error && <Failure>{error}</Failure>}

        {!hasEvidence && priceEvidenceInputs}

        {selected && <Card>
          <CardHead><div><h2>The mark</h2>
            <p>What it is now, what is proposed, and what that does to the official market value.</p></div></CardHead>
          <Facts>
            <dt>Current</dt><dd>{pct(selected.current_mark_pct)} · {money(selected.current_market_value, ccy)}</dd>
            <dt>Proposed</dt><dd><strong>{pct(selected.proposed_mark_pct)}</strong> · {money(selected.proposed_market_value, ccy)}</dd>
            <dt>Change</dt><dd style={{ color: delta == null ? undefined : delta < 0 ? '#fca5a5' : '#7cddbe' }}>
              {delta == null ? '—' : `${delta >= 0 ? '+' : ''}${money(delta, ccy)}`}</dd>
            <dt>Classification</dt><dd>{plain(selected.classification)}</dd>
            <dt>Proposed by</dt><dd>{who(selected.created_by)} on {date(selected.created_at || selected.effective_date)}</dd>
            {selected.reviewed_by && <><dt>Reviewed by</dt><dd>{who(selected.reviewed_by)}</dd></>}
            {selected.approved_by && <><dt>Approved by</dt><dd>{who(selected.approved_by)}</dd></>}
            <dt>Official lineage</dt><dd>{selected.lifecycle_trade_id
              ? `Trade #${selected.lifecycle_trade_id}, superseding the prior position version`
              : 'Nothing posted; the position is unchanged'}</dd>
            {selected.rationale && <><dt>Rationale</dt><dd>{selected.rationale}</dd></>}
          </Facts>
        </Card>}

        {proposing && hasEvidence && <Card style={{ marginTop: selected ? 14 : 0 }}>
          <CardHead><div><h2>New proposal</h2>
            <p>Pick the evidence it rests on. The mark comes from that record, not from a
              number typed here.</p></div></CardHead>
          <Fields>
            <Field $wide>Price evidence
              <select value={priceEvidenceId} onChange={e => {
                const id = e.target.value; setPriceEvidenceId(id);
                setRationale('');
              }}>
                <option value="">{hasEvidence ? 'Select evidence' : 'No price evidence recorded yet'}</option>
                {evidence.map((item: any) => <option key={item.id} value={item.id}>
                  {item.provider_identifier} · {date(item.price_date)} · {item.evidence_mark_pct}% · {plain(item.data_quality)}
                </option>)}
              </select>
              {selectedPrice && <small>{humanizeToken(selectedPrice.evidence_type)} from {humanizeToken(selectedPrice.data_provider).toLowerCase()}. {selectedPrice.evidence_notes}</small>}
            </Field>
            <Field>Effective date
              <DateField value={asOfDate} onChange={setAsOfDate} />
              <small>{tapeAsOf
                ? `A mark rests on a released tape. The newest is as of ${date(tapeAsOf)}.`
                : 'This facility has no released tape, so a mark cannot be proposed yet.'}</small>
            </Field>
            <Field>Attach a scenario run
              <select value={scenarioId} onChange={e => setScenarioId(e.target.value)}>
                <option value="">{hasScenarios ? 'None' : 'No scenario definitions yet'}</option>
                {(model.scenarioDefinitions || []).map((item: any) => <option key={item.id} value={item.id}>{item.scenario_name} · v{item.version_no}</option>)}
              </select>
              <small>{scenario ? 'A run for this scenario and date will be linked.' : 'Optional supporting evidence.'}</small>
            </Field>
            <Field $wide>Rationale
              <textarea value={rationale} onChange={e => setRationale(e.target.value)}
                placeholder="Why this source supports the proposed mark and how it affects the facility." />
              <small>Write what you reviewed. Selecting a different price source clears this draft.</small>
            </Field>
          </Fields>
          <Row>
            <Button type="button" $variant="primary"
              disabled={Boolean(busy) || !canMake || !selectedPrice || !rationale.trim()}
              onClick={() => void act('propose', () => calculateFacilityValuation(facilityId, {
                effectiveDate: asOfDate,
                priceEvidenceId: Number(selectedPrice?.id),
                proposedMarkPct: Number(selectedPrice?.evidence_mark_pct),
                rationale,
                scenarioRunId: scenario?.id || null,
              }), 'Indicative valuation created; official IBOR is unchanged.')}>
              {busy === 'propose' ? 'Calculating…' : 'Propose mark change'}
            </Button>
            <span style={{ color: INK.faint, fontSize: 12 }}>
              {!canMake ? 'A maker role proposes a mark.'
                : !selectedPrice ? 'Pick the price evidence first.'
                : !rationale.trim() ? 'A rationale is required.'
                : 'Indicative until a second person approves it.'}
            </span>
          </Row>
        </Card>}

        {decidable && <Card style={{ marginTop: 14 }}>
          <CardHead><div><h2>Valuation decision</h2>
            <p>{separateReviewerRequired
              ? `You proposed this run, so you cannot decide it. It needs an actor other than ${selected.created_by}.`
              : 'Record what you reviewed. Approving posts the mark; challenging leaves the position unchanged.'}</p></div></CardHead>
          <Fields>
            <Field $wide>Decision evidence
              <textarea value={decisionNotes} onChange={e => setDecisionNotes(e.target.value)}
                placeholder="Source, method and impact reviewed." />
            </Field>
          </Fields>
        </Card>}

        {(hasEvidence || hasScenarios) && <Grid2>
          <Card>
            <CardHead><div><h2>Stress</h2><p>Scenarios are decision support. A run freezes its assumptions and changes no authority.</p></div></CardHead>
            {!hasEvidence && <Fields><Field>Scenario date<DateField value={asOfDate} onChange={setAsOfDate} /></Field><Field>Scenario<select value={scenarioId} onChange={e => setScenarioId(e.target.value)}><option value="">Select a scenario</option>{(model.scenarioDefinitions || []).map((item: any) => <option key={item.id} value={item.id}>{item.scenario_name} · v{item.version_no}</option>)}</select></Field></Fields>}
            {scenario ? <Facts>
              <dt>Scenario</dt><dd>{scenario.scenario_name}</dd>
              <dt>Stressed loss</dt><dd style={{ color: '#fca5a5' }}>{money(scenario.stressed_loss, ccy)}</dd>
              <dt>Stressed base</dt><dd>{money(scenario.stressed_borrowing_base, ccy)}</dd>
              <dt>Headroom</dt><dd style={{ color: Number(scenario.stressed_headroom) < 0 ? '#fca5a5' : '#7cddbe' }}>
                {money(scenario.stressed_headroom, ccy)}</dd>
            </Facts> : <p style={{ color: INK.muted, fontSize: 13 }}>
              No run for this scenario and date. Run one to see the stressed base and headroom.</p>}
            <Row style={{ marginTop: 12 }}>
              <SmallButton type="button" disabled={Boolean(busy) || !canMake || !scenarioId}
                onClick={() => void act('scenario', () => runFacilityScenario(facilityId, Number(scenarioId), asOfDate),
                  'Scenario evidence finalised; no economic authority changed.')}>
                {busy === 'scenario' ? 'Running…' : 'Run scenario'}
              </SmallButton>
            </Row>
          </Card>

          <Card>
            <CardHead><div><h2>Evidence on file</h2><p>Every price kept as its own record, with where it came from.</p></div></CardHead>
            {!hasEvidence ? <p style={{ color: INK.muted, fontSize: 13 }}>Nothing recorded yet.</p> : <Facts>
              {evidence.slice(0, 5).map((p: any) => <React.Fragment key={p.id}>
                <dt>{date(p.price_date)}</dt>
                <dd>{p.evidence_mark_pct}% · {plain(p.evidence_type)} · {plain(p.data_quality)}
                  <div style={{ color: INK.faint, fontSize: 12 }}>{p.provider_identifier} · {who(p.created_by)}</div></dd>
              </React.Fragment>)}
            </Facts>}
          </Card>
        </Grid2>}

        {hasEvidence && priceEvidenceInputs}

        <Signoff>
          <summary>Create a scenario definition <small>Optional assumptions for stress analysis</small></summary>
          <InputBody>
            <InputHeading>
              <h3>Scenario and assumptions</h3>
              <p>Define a reusable stress case for this tenant. Creating it does not propose or approve a mark.</p>
            </InputHeading>
            <Fields>
              <Field>Scenario code<input value={scenarioCode} onChange={e => setScenarioCode(e.target.value)} placeholder="CLIENT_ADVERSE_01" /></Field>
              <Field>Scenario name<input value={scenarioName} onChange={e => setScenarioName(e.target.value)} placeholder="Client-approved adverse case" /></Field>
              <Field>Severity<select value={scenarioSeverity} onChange={e => setScenarioSeverity(e.target.value)}>
                <option value="ADVERSE">Adverse</option><option value="SEVERE">Severe</option><option value="BASE">Base</option>
              </select></Field>
              <Field>CDR %<input type="number" min="0" max="100" step="0.01" value={scenarioCdr} onChange={e => setScenarioCdr(e.target.value)} /></Field>
              <Field>Loss severity %<input type="number" min="0" max="100" step="0.01" value={scenarioLossSeverity} onChange={e => setScenarioLossSeverity(e.target.value)} /></Field>
              <Field>Eligibility haircut %<input type="number" min="0" max="100" step="0.01" value={scenarioHaircut} onChange={e => setScenarioHaircut(e.target.value)} /></Field>
            </Fields>
            <InputActions>
              <Button type="button" disabled={Boolean(busy) || !canConfigureInputs || !scenarioCode.trim() || !scenarioName.trim() || scenarioCdr === '' || scenarioLossSeverity === '' || scenarioHaircut === ''}
                onClick={() => void act('scenario-def', () => createTenantScenarioDefinition({
                  scenarioCode: scenarioCode.trim(), scenarioName: scenarioName.trim(), severity: scenarioSeverity,
                  assumptions: { cdrPct: Number(scenarioCdr), lossSeverityPct: Number(scenarioLossSeverity), eligibilityHaircutPct: Number(scenarioHaircut) },
                }), 'Scenario definition is active and available for controlled runs.')}>
                {busy === 'scenario-def' ? 'Creating…' : 'Create scenario definition'}
              </Button>
              {!canConfigureInputs && <span>A risk or tenant administrator creates scenario definitions.</span>}
            </InputActions>
          </InputBody>
        </Signoff>

        <Bar>
          <div className="state">
            {decidable
              ? <>Proposed by <strong>{who(selected.created_by)}</strong>. {separateReviewerRequired ? 'Tenant policy requires a different reviewer.' : 'An authorized reviewer can decide it.'}</>
              : official
                ? <>Official mark <strong>{pct(official.proposed_mark_pct)}</strong>, approved by {who(official.approved_by)}.</>
                : <>No revaluation has been approved. The current IBOR mark is shown above.</>}
          </div>
          <div className="actions">
            {decidable && <>
              <Button type="button" $variant="primary"
                disabled={Boolean(busy) || !canCheck || separateReviewerRequired || !decisionNotes.trim()}
                onClick={() => void act('approve', () => decideFacilityValuation(facilityId, selected.id, 'APPROVE', decisionNotes),
                  'Official mark approved and posted through the MTM lifecycle lane.')}>
                {busy === 'approve' ? 'Approving…' : 'Approve official mark'}
              </Button>
              {selected.state === 'CALCULATED' && <Button type="button" $variant="danger"
                disabled={Boolean(busy) || !canCheck || separateReviewerRequired || !decisionNotes.trim()}
                onClick={() => void act('challenge', () => decideFacilityValuation(facilityId, selected.id, 'CHALLENGE', decisionNotes),
                  'Mark proposal challenged; official IBOR is unchanged.')}>
                <AlertTriangle size={14} /> Challenge
              </Button>}
            </>}
          </div>
        </Bar>
      </Work>
    </Layout>
  </Shell>;
};

export default ValuationWorkspace;
